import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Table } from '../../../components/ui/Table';
import toast from 'react-hot-toast';
import { FiUser, FiAlertCircle } from 'react-icons/fi';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { loadAllGuardsForExport, GuardData } from '../../manage_guard/service/guardService';
import {
  saveDeployment,
  saveSingleSlotDeployment,
  saveHistoricalDeployment,
  getGuardCurrentDeployment,
  getClientCurrentDeployment,
  DeploymentGuard,
  DeploymentData,
} from '../service/deploymentService';
import { useAuth } from '../../../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../../firebase/config';

import { Select } from '../../../components/ui/Select';

const ModalContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const StepContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const StepTitle = styled.h3`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const SearchContainer = styled.div`
  margin-bottom: ${theme.spacing[3]};
`;

const GuardSelectionContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${theme.spacing[4]};
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const GuardSlot = styled.div`
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  background-color: ${theme.colors.white};
  min-height: 200px;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
  box-shadow: ${theme.shadows.md};
  transition: all 0.2s ease;

  &:hover {
    box-shadow: ${theme.shadows.lg};
    transform: translateY(-2px);
  }
`;

const GuardSlotHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing[2]};
`;

const GuardSlotTitle = styled.div`
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.light.text.primary};
`;

const SelectedGuard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[4]};
  background: linear-gradient(135deg, ${theme.colors.primary[50]} 0%, ${theme.colors.white} 100%);
  border: 1px solid ${theme.colors.primary[200]};
  border-radius: ${theme.borderRadius.md};
  text-align: center;
  flex: 1;
`;

const GuardImage = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid ${theme.colors.primary[300]};
  flex-shrink: 0;
  box-shadow: ${theme.shadows.sm};
  display: block;
  background-color: ${theme.colors.light.background.tertiary};
`;

const GuardInfo = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  align-items: center;
`;

const GuardName = styled.div`
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.primary};
`;

const GuardId = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.primary[600]};
`;

const GuardCNIC = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

const GuardList = styled.div`
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid ${theme.colors.light.border.light};
  border-radius: ${theme.borderRadius.md};
  margin-top: ${theme.spacing[2]};
`;

const GuardItem = styled.div<{ disabled?: boolean }>`
  padding: ${theme.spacing[3]};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  border-bottom: 1px solid ${theme.colors.light.border.light};
  background-color: ${({ disabled }) =>
    disabled ? theme.colors.light.background.tertiary : theme.colors.white};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};

  &:hover {
    background-color: ${({ disabled }) =>
    disabled
      ? theme.colors.light.background.tertiary
      : theme.colors.primary[50]};
    transform: ${({ disabled }) => (disabled ? 'none' : 'translateX(4px)')};
  }

  &:last-child {
    border-bottom: none;
  }
`;

const GuardItemImage = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid ${theme.colors.primary[300]};
  flex-shrink: 0;
  box-shadow: ${theme.shadows.sm};
`;

const GuardItemInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const GuardItemName = styled.div`
  font-weight: ${theme.typography.fontWeight.semibold};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.primary};
`;

const GuardItemId = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.primary[600]};
`;

const GuardItemCNIC = styled.div`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.secondary};
`;

const EmptyStateText = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.secondary};
  margin: 0;
`;

const WarningBanner = styled.div`
  background-color: ${theme.colors.accent.warning}20;
  border: 1px solid ${theme.colors.accent.warning};
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing[3]};
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing[2]};
  margin-bottom: ${theme.spacing[3]};
`;

const WarningText = styled.div`
  flex: 1;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.primary};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const DatePickerContainer = styled.div`
  margin-top: ${theme.spacing[2]};
`;

const StyledDatePicker = styled(DatePicker)`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.base};
  background-color: ${theme.colors.white};
  color: ${theme.colors.light.text.primary};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
  }
`;

interface Client {
  id: string;
  name: string;
  type: 'Individual' | 'Factory' | 'Bank';
  address?: string;
  industryName?: string;
  bankName?: string;
  branchCode?: string;
  path: string; // Firestore path
  deployedGuards?: any[];
  requiredGuard?: number;
}

interface DeploymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  clients: Client[];
  clientType: 'Individual' | 'Factory' | 'Bank';
  onDeploymentSaved: () => void;
  initialClientPath?: string;
  initialSlot?: number;
  initialDate?: Date;
}

const DeploymentModal: React.FC<DeploymentModalProps> = ({
  isOpen,
  onClose,
  clients,
  clientType,
  onDeploymentSaved,
  initialClientPath,
  initialSlot,
  initialDate,
}) => {
  const { companyData, user } = useAuth();
  const [step, setStep] = useState<'client' | 'guards'>('client');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedGuards, setSelectedGuards] = useState<(DeploymentGuard | null)[]>([]);
  const [guards, setGuards] = useState<GuardData[]>([]);
  const [loading, setLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState('');
  const [guardSearch, setGuardSearch] = useState('');
  const [showGuardModal, setShowGuardModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<number | null>(null);
  const [deploymentDate, setDeploymentDate] = useState<Date>(new Date());
  const [guardWarnings, setGuardWarnings] = useState<Record<string, any>>({});
  const [warningGuard, setWarningGuard] = useState<{ guard: GuardData; slot: number } | null>(
    null
  );
  const [warningType, setWarningType] = useState<'regular' | 'ot' | null>(null);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const [clientCurrentDeployment, setClientCurrentDeployment] = useState<any>(null);

  const [selectedBank, setSelectedBank] = useState<string>('');
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [isHistoryMode, setIsHistoryMode] = useState(false);

  // Use company ID if available, otherwise fall back to user UID (same as service)
  const companyId = companyData?.id || auth.currentUser?.uid || '';

  useEffect(() => {
    if (isOpen) {
      loadGuards();
      resetForm();

      // Handle initial props
      if (initialClientPath) {
        const client = clients.find(c => c.path === initialClientPath);
        if (client) {
          handleClientSelect(client);
        }
      }

      if (initialDate) {
        setDeploymentDate(initialDate);
      }

      if (initialSlot !== undefined && initialSlot !== null) {
        setSelectedSlot(initialSlot);
        setShowGuardModal(true);
      }
    }
  }, [isOpen, initialClientPath, initialSlot, initialDate, clients]);

  useEffect(() => {
    if (selectedClient) {
      loadClientDeployment();
    }
  }, [selectedClient]);

  useEffect(() => {
    const loadExistingGuardsWithImages = async () => {
      // Ensure we have a valid company ID
      const validCompanyId = companyId || auth.currentUser?.uid;
      if (clientCurrentDeployment?.deployedGuards && validCompanyId) {
        // Fetch full guard data including images
        const guardsWithImages = await Promise.all(
          clientCurrentDeployment.deployedGuards.map(async (g: any) => {
            try {
              // Fetch full guard document to get image
              const guardRef = doc(db, 'guards', validCompanyId, 'companyGuards', g.id);
              const guardSnap = await getDoc(guardRef);

              if (guardSnap.exists()) {
                const guardData = guardSnap.data();
                console.log('Fetched full guard data:', guardData);
                return {
                  id: g.id,
                  name: g.name || guardData.name || g.guardId || 'Unknown',
                  guardId: g.guardId || guardData.guardId || '',
                  cnic: g.cnic || guardData.cnic || '',
                  image: guardData.image || g.image || undefined,
                  slotNumber: g.slotNumber // Preserve slot number
                };
              } else {
                // Fallback to deployment data if guard document not found
                return {
                  id: g.id,
                  name: g.name || g.guardId || 'Unknown',
                  guardId: g.guardId || '',
                  cnic: g.cnic || '',
                  image: g.image || undefined,
                  slotNumber: g.slotNumber
                };
              }
            } catch (error) {
              console.error('Error fetching guard data:', error);
              // Fallback to deployment data on error
              return {
                id: g.id,
                name: g.name || g.guardId || 'Unknown',
                guardId: g.guardId || '',
                cnic: g.cnic || '',
                image: g.image || undefined,
                slotNumber: g.slotNumber
              };
            }
          })
        );

        // Pre-populate selected guards with existing deployments using slotNumber
        const defaultRequired = clientType === 'Individual' ? 1 : 3;
        const requiredSlots = selectedClient?.requiredGuard || defaultRequired;

        // Find max slot number to size the array correctly
        const maxSlot = Math.max(requiredSlots, ...guardsWithImages.map((g: any) => g.slotNumber || 0));
        const newSelectedGuards = new Array(maxSlot).fill(null);

        guardsWithImages.forEach((guard: any) => {
          if (guard.slotNumber) {
            newSelectedGuards[guard.slotNumber - 1] = guard;
            console.log('Setting existing guard at slot', guard.slotNumber - 1, 'with image:', guard.image);
          }
        });
        setSelectedGuards(newSelectedGuards);
      }
    };

    loadExistingGuardsWithImages();
  }, [clientCurrentDeployment, companyId]);

  const resetForm = () => {
    setStep('client');
    setSelectedClient(null);
    setSelectedGuards([]);
    setClientSearch('');
    setGuardSearch('');
    setShowGuardModal(false);
    setSelectedSlot(null);
    setDeploymentDate(new Date());
    setGuardWarnings({});
    setShowWarningModal(false);
    setWarningGuard(null);
    setClientCurrentDeployment(null);
    setEndDate(null);
    setIsHistoryMode(false);
  };

  const loadGuards = async () => {
    try {
      const validCompanyId = companyId || auth.currentUser?.uid || '';
      const guardsData = await loadAllGuardsForExport(validCompanyId);
      setGuards(guardsData.filter((g) => g.isActive !== false));
    } catch (error: any) {
      console.error('Error loading guards:', error);
      toast.error('Failed to load guards');
    }
  };

  const loadClientDeployment = async () => {
    if (!selectedClient) return;
    try {
      const deployment = await getClientCurrentDeployment(selectedClient.path);
      setClientCurrentDeployment(deployment);

      // If we have slot end dates, suggest the next date for the selected slot if not already set by props
      if (!initialDate) {
        const clientRef = doc(db, selectedClient.path);
        const clientSnap = await getDoc(clientRef);
        if (clientSnap.exists()) {
          const clientData = clientSnap.data();
          if (clientData.slotEndDates && selectedSlot !== null) {
            const lastEndDate = clientData.slotEndDates[selectedSlot + 1];
            if (lastEndDate) {
              const nextDay = new Date(lastEndDate.seconds ? lastEndDate.seconds * 1000 : lastEndDate);
              nextDay.setDate(nextDay.getDate() + 1);
              setDeploymentDate(nextDay);
            }
          }
        }
      }
    } catch (error: any) {
      console.error('Error loading client deployment:', error);
    }
  };

  const uniqueBanks = useMemo(() => {
    if (clientType !== 'Bank') return [];
    const banksSet = new Set<string>();
    clients.forEach((c) => {
      if (c.bankName) banksSet.add(c.bankName);
    });
    return Array.from(banksSet).sort();
  }, [clients, clientType]);

  const filteredClients = useMemo(() => {
    let filtered = clients;

    if (clientType === 'Bank' && selectedBank) {
      filtered = filtered.filter((c) => c.bankName === selectedBank);
    }

    if (!clientSearch.trim()) return filtered;
    const term = clientSearch.toLowerCase();
    return filtered.filter((c) => {
      const name = c.name?.toLowerCase() || '';
      const address = c.address?.toLowerCase() || '';
      const industryName = c.industryName?.toLowerCase() || '';
      const bankName = c.bankName?.toLowerCase() || '';
      const branchCode = c.branchCode ? String(c.branchCode).toLowerCase() : '';
      return (
        name.includes(term) ||
        address.includes(term) ||
        industryName.includes(term) ||
        bankName.includes(term) ||
        branchCode.includes(term)
      );
    });
  }, [clients, clientSearch, selectedBank, clientType]);

  const availableGuards = useMemo(() => {
    if (selectedSlot === null) return guards;

    const isOTSelection = selectedSlot >= 100;

    const selectedIdsInOtherSlotsOfSameType = selectedGuards
      .filter((g, index) => {
        if (g === null || index === selectedSlot) return false;
        const isOtherSlotOT = index >= 100;
        return isOtherSlotOT === isOTSelection;
      })
      .map((g) => g!.id);

    return guards.filter((g) => !selectedIdsInOtherSlotsOfSameType.includes(g.id));
  }, [guards, selectedGuards, selectedSlot]);

  const filteredGuards = useMemo(() => {
    if (!guardSearch.trim()) return availableGuards;
    const term = guardSearch.toLowerCase();
    return availableGuards.filter((g) => {
      const name = g.name?.toLowerCase() || '';
      const guardId = g.guardId?.toLowerCase() || '';
      const cnic = g.cnic?.toLowerCase() || '';
      return name.includes(term) || guardId.includes(term) || cnic.includes(term);
    });
  }, [availableGuards, guardSearch]);

  const handleClientSelect = (client: Client) => {
    setSelectedClient(client);
    const defaultRequired = clientType === 'Individual' ? 1 : 3;
    setSelectedGuards(new Array(client.requiredGuard || defaultRequired).fill(null));
    setStep('guards');
  };

  const handleGuardSelect = async (guard: GuardData) => {
    if (selectedSlot === null) return;

    // Check if guard is already deployed
    try {
      const validCompanyId = companyId || auth.currentUser?.uid || '';
      const deployments = await getGuardCurrentDeployment(guard.id, validCompanyId);

      const isOTSlot = selectedSlot >= 100; // OT slots are 101, 102, etc. (index 100, 101...)
      const existingRegular = deployments.currentDeployment;
      const existingOT = deployments.currentOTDeployment;

      const currentActive = isOTSlot ? existingOT : existingRegular;

      if (currentActive && currentActive.postDate) {
        const activeStartDate = currentActive.postDate.seconds
          ? new Date(currentActive.postDate.seconds * 1000)
          : new Date(currentActive.postDate);

        // If selected deployment date is BEFORE active deployment start date
        if (deploymentDate < activeStartDate) {
          setIsHistoryMode(true);
          // Suggest end date as one day before active starts
          const suggestedEndDate = new Date(activeStartDate);
          suggestedEndDate.setDate(suggestedEndDate.getDate() - 1);
          setEndDate(suggestedEndDate);
          toast.success('Historical mode activated. Suggested end date set.');
        } else {
          // Normal conflict check
          if (!isOTSlot && existingRegular) {
            setWarningGuard({ guard, slot: selectedSlot });
            setWarningType('regular');
            setShowGuardModal(false);
            setShowWarningModal(true);
            return;
          }

          if (isOTSlot && existingOT) {
            setWarningGuard({ guard, slot: selectedSlot });
            setWarningType('ot');
            setShowGuardModal(false);
            setShowWarningModal(true);
            return;
          }
        }
      }
    } catch (error) {
      console.error('Error checking guard deployment:', error);
    }

    // If not deployed, select directly
    const newSelectedGuards = [...selectedGuards];
    newSelectedGuards[selectedSlot] = {
      id: guard.id,
      name: guard.name || guard.guardId || 'Unknown',
      guardId: guard.guardId || '',
      cnic: guard.cnic || '',
      image: guard.image || undefined,
      slotNumber: selectedSlot + 1, // Include 1-indexed slot number
    };
    console.log('Setting guard with image URL:', guard.image);
    console.log('Selected guard object:', newSelectedGuards[selectedSlot]);
    setSelectedGuards(newSelectedGuards);
    setShowGuardModal(false);
    setSelectedSlot(null);
    setGuardSearch('');
  };

  const handleConfirmGuardChange = () => {
    if (!warningGuard) return;
    const { guard, slot } = warningGuard;
    const newSelectedGuards = [...selectedGuards];
    newSelectedGuards[slot] = {
      id: guard.id,
      name: guard.name || guard.guardId || 'Unknown',
      guardId: guard.guardId || '',
      cnic: guard.cnic || '',
      image: guard.image,
      slotNumber: slot + 1, // Include 1-indexed slot number
    };
    setSelectedGuards(newSelectedGuards);
    setShowGuardModal(false);
    setSelectedSlot(null);
    setGuardSearch('');
    setShowWarningModal(false);
    setWarningGuard(null);
  };


  const handleSave = async () => {
    if (!selectedClient) {
      toast.error('Please select a client');
      return;
    }

    const validGuards = selectedGuards.filter((g) => g !== null) as DeploymentGuard[];
    if (validGuards.length === 0) {
      toast.error('Please select at least one guard');
      return;
    }

    setLoading(true);
    try {
      const deploymentData: DeploymentData = {
        clientId: selectedClient.id,
        clientType: selectedClient.type,
        clientName: selectedClient.name,
        clientPath: selectedClient.path,
        guards: validGuards,
        deploymentDate: deploymentDate,
      };

      const validCompanyId = companyId || auth.currentUser?.uid || '';
      if (!validCompanyId) {
        throw new Error('Company ID or user authentication required');
      }

      if (isHistoryMode && endDate) {
        await saveHistoricalDeployment(
          {
            ...deploymentData,
            endDate,
          },
          validCompanyId
        );
      } else if (initialSlot !== undefined && initialSlot !== null) {
        // Single slot mode
        const guard = selectedGuards[initialSlot];
        if (!guard) {
          toast.error('Please select a guard');
          return;
        }
        await saveSingleSlotDeployment(
          selectedClient.path,
          guard,
          deploymentDate,
          initialSlot + 1,
          validCompanyId,
          {
            clientId: selectedClient.id,
            clientName: selectedClient.name,
            clientType: selectedClient.type,
          }
        );
      } else {
        // Full deployment mode
        const deploymentData: DeploymentData = {
          clientId: selectedClient.id,
          clientType: selectedClient.type,
          clientName: selectedClient.name,
          clientPath: selectedClient.path,
          guards: validGuards,
          deploymentDate: deploymentDate,
        };
        await saveDeployment(deploymentData, validCompanyId);
      }

      toast.success('Deployment saved successfully');
      onDeploymentSaved();
      onClose();
      resetForm();
    } catch (error: any) {
      console.error('Error saving deployment:', error);
      toast.error(error.message || 'Failed to save deployment');
    } finally {
      setLoading(false);
    }
  };

  const hasAllGuardsDeployed = selectedClient && clientCurrentDeployment?.deployedGuards?.length >= (selectedClient.requiredGuard || (clientType === 'Individual' ? 1 : 3));

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={step === 'client' ? 'Select Client' : 'Select Guards'}
        size="lg"
        footer={
          <div style={{ display: 'flex', gap: theme.spacing[2], justifyContent: 'flex-end' }}>
            {step === 'guards' && (
              <Button variant="secondary" onClick={() => setStep('client')}>
                Back
              </Button>
            )}
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            {step === 'guards' && (
              <Button onClick={handleSave} disabled={loading} loading={loading}>
                Save Deployment
              </Button>
            )}
          </div>
        }
      >
        <ModalContent>
          {step === 'client' && (
            <StepContainer>
              <StepTitle>Select a Client</StepTitle>
              <SearchContainer style={{ display: 'flex', gap: theme.spacing[2] }}>
                {clientType === 'Bank' && (
                  <div style={{ flex: '0 0 30%' }}>
                    <Select
                      value={selectedBank}
                      onChange={(e) => setSelectedBank(e.target.value)}
                      dark
                      fullWidth
                    >
                      <option value="">All Banks</option>
                      {uniqueBanks.map((bank) => (
                        <option key={bank} value={bank}>{bank}</option>
                      ))}
                    </Select>
                  </div>
                )}
                <div style={{ flex: 1 }}>
                  <Input
                    placeholder="Search clients..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    dark
                    fullWidth
                  />
                </div>
              </SearchContainer>
              <div style={{ maxHeight: '400px', overflowY: 'auto', border: `1px solid ${theme.colors.light.border.light}`, borderRadius: theme.borderRadius.md }}>
                {filteredClients.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: theme.spacing[4], color: theme.colors.light.text.secondary }}>
                    No clients found
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    {filteredClients.map((client) => {
                      const deployedCount = client.deployedGuards?.length || 0;
                      const requiredCount = client.requiredGuard || 3;
                      const balance = Math.max(0, requiredCount - deployedCount);

                      return (
                        <div
                          key={client.id}
                          onClick={() => handleClientSelect(client)}
                          style={{
                            padding: theme.spacing[3],
                            borderBottom: `1px solid ${theme.colors.light.border.light}`,
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: theme.spacing[1]
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.colors.light.background.secondary}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          <div style={{ fontWeight: theme.typography.fontWeight.bold, color: theme.colors.light.text.primary, fontSize: theme.typography.fontSize.base }}>
                            {client.type === 'Factory' && client.industryName
                              ? `${client.industryName} - ${client.name}`
                              : client.type === 'Bank' && client.bankName
                                ? `${client.bankName} ${client.branchCode ? `- ${client.branchCode}` : ''}`
                                : client.name}
                          </div>
                          <div style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.light.text.secondary }}>
                            {client.address || '—'}
                          </div>
                          <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.primary[600], marginTop: theme.spacing[1], fontWeight: theme.typography.fontWeight.medium }}>
                            Deployed: {deployedCount} / Required: {requiredCount} (Balance: {balance})
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            </StepContainer>
          )}

          {step === 'guards' && selectedClient && (
            <StepContainer>
              <StepTitle>
                Select Guards for{' '}
                {selectedClient.type === 'Factory' && selectedClient.industryName
                  ? `${selectedClient.industryName} - ${selectedClient.name}`
                  : selectedClient.type === 'Bank' && selectedClient.bankName
                    ? `${selectedClient.bankName} ${selectedClient.branchCode ? `- ${selectedClient.branchCode}` : ''}`
                    : selectedClient.name}
              </StepTitle>

              {hasAllGuardsDeployed && (
                <WarningBanner>
                  <FiAlertCircle size={20} />
                  <WarningText>
                    This client already has {selectedClient.requiredGuard || (clientType === 'Individual' ? 1 : 3)} guards deployed. You can change guards using the
                    "Change Guard" button below each guard slot.
                  </WarningText>
                </WarningBanner>
              )}

              <div style={{ display: 'flex', gap: theme.spacing[4], alignItems: 'flex-end', marginBottom: theme.spacing[4] }}>
                <DatePickerContainer style={{ flex: 1, marginTop: 0 }}>
                  <label
                    style={{
                      display: 'block',
                      fontSize: theme.typography.fontSize.sm,
                      fontWeight: theme.typography.fontWeight.medium,
                      color: theme.colors.light.text.primary,
                      marginBottom: theme.spacing[1],
                    }}
                  >
                    Deployment Date
                  </label>
                  <StyledDatePicker
                    selected={deploymentDate}
                    onChange={(date: Date) => setDeploymentDate(date)}
                    dateFormat="dd/MM/yyyy"
                  />
                </DatePickerContainer>

                {isHistoryMode && (
                  <DatePickerContainer style={{ flex: 1, marginTop: 0 }}>
                    <label
                      style={{
                        display: 'block',
                        fontSize: theme.typography.fontSize.sm,
                        fontWeight: theme.typography.fontWeight.medium,
                        color: theme.colors.light.text.primary,
                        marginBottom: theme.spacing[1],
                      }}
                    >
                      End Date (Historical)
                    </label>
                    <StyledDatePicker
                      selected={endDate}
                      onChange={(date: Date) => setEndDate(date)}
                      dateFormat="dd/MM/yyyy"
                      minDate={deploymentDate}
                    />
                  </DatePickerContainer>
                )}
              </div>

              <GuardSelectionContainer>
                {selectedGuards.map((_, slot) => {
                  if (initialSlot !== undefined && initialSlot !== null && slot !== initialSlot) {
                    return null;
                  }

                  const requiredCount = selectedClient?.requiredGuard || (clientType === 'Individual' ? 1 : 3);
                  const isOTSlot = slot >= 100;
                  const selectedGuard = selectedGuards[slot];

                  // Only show required regular slots or slots that have a guard assigned
                  if (!isOTSlot && slot >= requiredCount && !selectedGuard) return null;
                  if (isOTSlot && !selectedGuard && slot !== initialSlot) return null;

                  return (
                    <GuardSlot key={slot}>
                      <GuardSlotHeader>
                        <GuardSlotTitle>
                          {isOTSlot ? `OT Slot ${slot - 99}` : `Guard Slot ${slot + 1}`}
                        </GuardSlotTitle>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setSelectedSlot(slot);
                            setShowGuardModal(true);
                            setGuardSearch('');
                          }}
                        >
                          {selectedGuard ? 'Change Guard' : (hasAllGuardsDeployed ? 'Change Guard' : 'Select Guard')}
                        </Button>
                      </GuardSlotHeader>

                      {selectedGuard ? (
                        <SelectedGuard>
                          {selectedGuard.image ? (
                            <GuardImage
                              key={`${selectedGuard.id}-${selectedGuard.image}`}
                              src={selectedGuard.image}
                              alt={selectedGuard.name}
                              onLoad={() => {
                                console.log('✅ Guard image loaded:', selectedGuard.image);
                              }}
                              onError={(e) => {
                                console.error('❌ Guard image failed to load:', selectedGuard.image, e);
                                const target = e.target as HTMLImageElement;
                                const parent = target.parentElement;
                                if (parent) {
                                  target.style.display = 'none';
                                  const placeholder = document.createElement('div');
                                  placeholder.style.cssText = `
                                      width: 80px;
                                      height: 80px;
                                      border-radius: 50%;
                                      background-color: ${theme.colors.light.background.tertiary};
                                      display: flex;
                                      align-items: center;
                                      justify-content: center;
                                      border: 3px solid ${theme.colors.primary[300]};
                                      flex-shrink: 0;
                                    `;
                                  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                                  icon.setAttribute('width', '40');
                                  icon.setAttribute('height', '40');
                                  icon.setAttribute('viewBox', '0 0 24 24');
                                  icon.setAttribute('fill', 'none');
                                  icon.setAttribute('stroke', theme.colors.light.text.muted);
                                  icon.setAttribute('stroke-width', '2');
                                  const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                                  path1.setAttribute('d', 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2');
                                  const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
                                  circle.setAttribute('cx', '12');
                                  circle.setAttribute('cy', '7');
                                  circle.setAttribute('r', '4');
                                  icon.appendChild(path1);
                                  icon.appendChild(circle);
                                  placeholder.appendChild(icon);
                                  parent.appendChild(placeholder);
                                }
                              }}
                            />
                          ) : (
                            <div
                              style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '50%',
                                backgroundColor: theme.colors.light.background.tertiary,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: `3px solid ${theme.colors.primary[300]}`,
                                flexShrink: 0,
                              }}
                            >
                              <FiUser size={40} style={{ color: theme.colors.light.text.muted }} />
                            </div>
                          )}
                          <GuardInfo>
                            <GuardName style={{ color: '#000000', fontWeight: 'bold' }}>{selectedGuard.name}</GuardName>
                            {selectedGuard.guardId && (
                              <GuardId>ID: {selectedGuard.guardId}</GuardId>
                            )}
                            {selectedGuard.cnic && (
                              <GuardCNIC>CNIC: {selectedGuard.cnic}</GuardCNIC>
                            )}
                          </GuardInfo>
                        </SelectedGuard>
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: theme.colors.light.text.muted,
                            padding: theme.spacing[4],
                            gap: theme.spacing[2],
                            flex: 1,
                          }}
                        >
                          <div
                            style={{
                              width: '80px',
                              height: '80px',
                              borderRadius: '50%',
                              backgroundColor: theme.colors.light.background.tertiary,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: `2px dashed ${theme.colors.light.border.medium}`,
                            }}
                          >
                            <FiUser size={40} style={{ color: theme.colors.light.border.medium }} />
                          </div>
                          <div style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium }}>
                            No guard selected
                          </div>
                        </div>
                      )}
                    </GuardSlot>
                  );
                })}
              </GuardSelectionContainer>
            </StepContainer>
          )}
        </ModalContent>
      </Modal>

      {/* Guard Selection Modal */}
      <Modal
        isOpen={showGuardModal}
        onClose={() => {
          setShowGuardModal(false);
          setSelectedSlot(null);
          setGuardSearch('');
        }}
        title={`Select Guard for Slot ${selectedSlot !== null ? selectedSlot + 1 : ''}`}
        size="lg"
        footer={
          <div style={{ display: 'flex', gap: theme.spacing[2], justifyContent: 'flex-end' }}>
            <Button
              variant="secondary"
              onClick={() => {
                setShowGuardModal(false);
                setSelectedSlot(null);
                setGuardSearch('');
              }}
            >
              Cancel
            </Button>
          </div>
        }
      >
        <ModalContent>
          <SearchContainer>
            <Input
              placeholder="Search guards by name, ID, or CNIC..."
              value={guardSearch}
              onChange={(e) => setGuardSearch(e.target.value)}
              dark
              fullWidth
            />
          </SearchContainer>
          <GuardList style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {filteredGuards.length === 0 ? (
              <div style={{ padding: theme.spacing[4], textAlign: 'center' }}>
                <EmptyStateText>No guards available</EmptyStateText>
              </div>
            ) : (
              filteredGuards.map((guard) => (
                <GuardItem
                  key={guard.id}
                  onClick={() => handleGuardSelect(guard)}
                >
                  {guard.image ? (
                    <GuardItemImage
                      src={guard.image}
                      alt={guard.name || guard.guardId}
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        const parent = target.parentElement;
                        if (parent) {
                          target.style.display = 'none';
                          const placeholder = document.createElement('div');
                          placeholder.style.cssText = `
                            width: 60px;
                            height: 60px;
                            border-radius: 50%;
                            background-color: ${theme.colors.light.background.tertiary};
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            border: 2px solid ${theme.colors.primary[300]};
                            flex-shrink: 0;
                          `;
                          const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
                          icon.setAttribute('width', '30');
                          icon.setAttribute('height', '30');
                          icon.setAttribute('viewBox', '0 0 24 24');
                          icon.setAttribute('fill', 'none');
                          icon.setAttribute('stroke', theme.colors.light.text.muted);
                          icon.setAttribute('stroke-width', '2');
                          const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
                          path1.setAttribute('d', 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2');
                          const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
                          circle.setAttribute('cx', '12');
                          circle.setAttribute('cy', '7');
                          circle.setAttribute('r', '4');
                          icon.appendChild(path1);
                          icon.appendChild(circle);
                          placeholder.appendChild(icon);
                          parent.appendChild(placeholder);
                        }
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: '60px',
                        height: '60px',
                        borderRadius: '50%',
                        backgroundColor: theme.colors.light.background.tertiary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `2px solid ${theme.colors.primary[300]}`,
                        flexShrink: 0,
                      }}
                    >
                      <FiUser size={30} style={{ color: theme.colors.light.text.muted }} />
                    </div>
                  )}
                  <GuardItemInfo>
                    <GuardItemName>{guard.name || 'Unknown'}</GuardItemName>
                    {guard.guardId && (
                      <GuardItemId>ID: {guard.guardId}</GuardItemId>
                    )}
                    {guard.cnic && (
                      <GuardItemCNIC>CNIC: {guard.cnic}</GuardItemCNIC>
                    )}
                    {!guard.cnic && !guard.guardId && (
                      <GuardItemCNIC>No additional info</GuardItemCNIC>
                    )}
                  </GuardItemInfo>
                </GuardItem>
              ))
            )}
          </GuardList>
        </ModalContent>
      </Modal>

      {/* Warning Modal for Guard Already Deployed */}
      <Modal
        isOpen={showWarningModal}
        onClose={() => {
          setShowWarningModal(false);
          setWarningGuard(null);
        }}
        title={warningType === 'ot' ? 'OT Guard Already Deployed' : 'Guard Already Deployed'}
        size="md"
        footer={
          <div style={{ display: 'flex', gap: theme.spacing[2], justifyContent: 'flex-end' }}>
            <Button
              variant="secondary"
              onClick={() => {
                setShowWarningModal(false);
                setWarningGuard(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmGuardChange}>Yes, Change Deployment</Button>
          </div>
        }
      >
        {warningGuard && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing[3], color: '#000000' }}>
            <WarningBanner style={{ color: '#000000' }}>
              <FiAlertCircle size={20} />
              <div className="alert alert-warning" style={{ color: '#000000' }}>
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                This guard is already deployed in a <strong>{warningType === 'ot' ? 'OT' : 'regular'}</strong> slot for another client.
                Moving them will terminate their previous <strong>{warningType === 'ot' ? 'OT' : 'regular'}</strong> assignment.
              </div>
              <p style={{ color: '#000000', margin: 0 }}>Do you want to proceed with this deployment?</p>
            </WarningBanner>
            <div style={{ color: '#000000' }}>
              <strong style={{ color: '#000000' }}>Guard:</strong>{' '}
              {warningGuard.guard.name || warningGuard.guard.guardId}
            </div>
            <div style={{ color: '#000000' }}>
              <strong style={{ color: '#000000' }}>CNIC:</strong>{' '}
              {warningGuard.guard.cnic || 'N/A'}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default DeploymentModal;

